//@ts-check
/**
 * Island enrichment helper functions.
 * Enriches island data with student entries from the datastore.
 */

import { IslandHeaders } from "@/types";

/**
 * Enriches a single island with student reports from entries.
 * @param {Object} island - The island object to enrich
 * @param {Array<import("@/types/index.ts").StudentEntry>} entries - Array of student entries
 * @returns {import("@/types/index.ts").EnrichedIsland} Enriched island with Reports array
 */
export function enrichSingleIsland(island, entries) {
  if (!island || !entries || !Array.isArray(entries)) {
    return island;
  }

  const islandId = island[IslandHeaders.ID];
  if (!islandId) {
    return island;
  }

  // Filter entries that match this island
  const matchingEntries = entries.filter((entry) => {
    if (!entry || !entry.value) return false;

    try {
      const parsedValue =
        typeof entry.value === "string" ? JSON.parse(entry.value) : entry.value;
      return parsedValue?.IslandId === islandId;
    } catch (e) {
      console.warn("Failed to parse entry value:", e);
      return false;
    }
  });

  // Transform entries into StudentReport format
  const reports = matchingEntries.map((entry) => {
    let parsedValue;
    try {
      parsedValue =
        typeof entry.value === "string" ? JSON.parse(entry.value) : entry.value;
    } catch (e) {
      console.warn("Failed to parse entry value for report:", e);
      parsedValue = {};
    }

    const reportDetails = parsedValue?.ReportDetails || {};

    return {
      onid: entry.onid || "",
      fullName: entry.fullname || "",
      reportDetails: reportDetails
    };
  });

  // Return enriched island with Reports array
  return {
    ...island,
    Reports: reports
  };
}

/**
 * Enriches all islands with student reports from entries.
 * @param {Array} islands - Array of island objects
 * @param {Array} entries - Array of entry objects from datastore
 * @returns {Array} Array of enriched islands, each with Reports array
 */
export function enrichAllIslands(islands, entries) {
  if (!islands || !Array.isArray(islands)) {
    return [];
  }

  if (!entries || !Array.isArray(entries)) {
    // Return islands with empty Reports arrays if no entries
    return islands.map((island) => ({
      ...island,
      Reports: island.Reports || []
    }));
  }

  // Enrich each island
  return islands.map((island) => enrichSingleIsland(island, entries));
}
